import { IBalusterGeometry, IRailingGeometry, IStairCalculationResult, IStepGeometry, IVec3 } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    ETextRole,
    FONT_MONO_FAMILY,
    IFont,
    IPlanPrimitive,
    IPoint,
    ITextPrimitive,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

/** Pane-local world-to-pixel transform of the top view. */
export interface IPlanProjector {
    pwx(x: number): number;
    pwy(z: number): number;
    pws(s: number): number;
}

/** A point of the stair in plan, stair millimetres. */
export interface IStairPoint {
    readonly x: number;
    readonly z: number;
}

/** A tread's two side edges — along its run — each from back to front, stair millimetres. */
export interface ITreadSides {
    readonly sides: readonly (readonly IStairPoint[])[];
    readonly centre: IStairPoint;
}

/** A length of a tread edge or a railing run and the plan point it is written at. */
export interface IPlanMark {
    readonly value: number;
    readonly at: IStairPoint;
}

/** A side edge shorter than this has no length worth writing — a winder's inner point (mm). */
const MIN_EDGE: number = 20;
/** How far inside the tread an edge length stands from its edge (mm). */
const EDGE_INSET: number = 70;
/** How far out from the stair a flight length dimension stands (px). */
const FLIGHT_DIM_GAP: number = 22;
/** How far a railing run length stands out past the handrail (px). */
const RUN_MARK_GAP: number = 12;
/** How far a post letter stands off the post's corner (px). */
const LETTER_GAP: number = 3;

const MARK_FONT: IFont = { weight: 400, size: 8, family: FONT_MONO_FAMILY };
const LETTER_FONT: IFont = { weight: 600, size: 10, family: FONT_MONO_FAMILY };
const NOTE_FONT: IFont = { weight: 400, size: 9, family: FONT_MONO_FAMILY };
const WALK_HEAD_LEN: number = 9;
const WALK_DASH: readonly number[] = [6, 4];

function polylineLength(points: readonly IStairPoint[]): number {
    let length: number = 0;
    for (let i: number = 1; i < points.length; i++) {
        length += Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
    }
    return length;
}

/** The point half way along a polyline. */
function halfway(points: readonly IStairPoint[]): IStairPoint {
    let left: number = polylineLength(points) / 2;
    for (let i: number = 1; i < points.length; i++) {
        const piece: number = Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
        if (piece >= left && piece > 0) {
            const t: number = left / piece;
            return { x: points[i - 1].x + (points[i].x - points[i - 1].x) * t, z: points[i - 1].z + (points[i].z - points[i - 1].z) * t };
        }
        left -= piece;
    }
    return points[points.length - 1];
}

/** The four plan corners of an ordinary tread, as the top view draws it: back left, back right, front right, front left. */
function treadCorners(step: IStepGeometry): IStairPoint[] {
    const { x, z } = step.position;
    if (step.rotation === 0) {
        return [
            { x, z },
            { x: x + step.treadWidth, z },
            { x: x + step.treadWidth, z: z + step.treadDepth },
            { x, z: z + step.treadDepth },
        ];
    }
    const cx: number = x + step.treadWidth / 2;
    const cz: number = z + (step.treadDepth + step.noseLength) / 2 - step.noseLength;
    const theta: number = (step.rotation * Math.PI) / 180;
    const corner: (rx: number, rz: number) => IStairPoint = (rx: number, rz: number): IStairPoint => ({
        x: cx + rx * Math.cos(theta) + rz * Math.sin(theta),
        z: cz - rx * Math.sin(theta) + rz * Math.cos(theta),
    });
    const halfW: number = step.treadWidth / 2;
    const halfD: number = step.treadDepth / 2;
    return [corner(-halfW, -halfD), corner(halfW, -halfD), corner(halfW, halfD), corner(-halfW, halfD)];
}

/** A tread's side edges and centre: an ordinary tread's left and right edges, a winder's inner and outer edges. */
export function treadSides(step: IStepGeometry): ITreadSides {
    const corners: IStepGeometry['winderCorners'] = step.winderCorners;
    if (corners) {
        const inner: IStairPoint[] = [corners.innerBack, ...[...(corners.innerMidPoints ?? [])].reverse(), corners.innerFront];
        const outer: IStairPoint[] = [corners.outerBack, ...(corners.outerMidPoints ?? []), corners.outerFront];
        const all: IStairPoint[] = [corners.innerBack, corners.innerFront, corners.outerBack, corners.outerFront];
        const centre: IStairPoint = {
            x: all.reduce((sum: number, point: IStairPoint) => sum + point.x, 0) / 4,
            z: all.reduce((sum: number, point: IStairPoint) => sum + point.z, 0) / 4,
        };
        return { sides: [inner, outer], centre };
    }
    const [backLeft, backRight, frontRight, frontLeft]: IStairPoint[] = treadCorners(step);
    const centre: IStairPoint = { x: (backLeft.x + frontRight.x) / 2, z: (backLeft.z + frontRight.z) / 2 };
    return {
        sides: [
            [backLeft, frontLeft],
            [backRight, frontRight],
        ],
        centre,
    };
}

/**
 * The going of every tread written at both its side edges, as a stair maker marks a plan: an
 * ordinary tread takes the flight's going, a winder the length of each of its edges. A landing
 * and an edge that shrinks to a point carry none.
 */
export function treadEdgeMarks(steps: readonly IStepGeometry[], going: number): IPlanMark[] {
    return steps
        .filter((step: IStepGeometry) => !step.isLanding)
        .flatMap((step: IStepGeometry) => {
            const { sides, centre } = treadSides(step);
            return sides
                .map((side: readonly IStairPoint[]) => ({ side, length: polylineLength(side) }))
                .filter(({ length }: { length: number }) => length >= MIN_EDGE)
                .map(({ side, length }: { side: readonly IStairPoint[]; length: number }): IPlanMark => {
                    const middle: IStairPoint = halfway(side);
                    const inward: number = Math.hypot(centre.x - middle.x, centre.z - middle.z);
                    const t: number = Math.min(0.5, EDGE_INSET / Math.max(1, inward));
                    return {
                        value: Math.round(step.winderCorners ? length : going),
                        at: { x: middle.x + (centre.x - middle.x) * t, z: middle.z + (centre.z - middle.z) * t },
                    };
                });
        });
}

/** The walking line: through the middle of every tread and landing, bottom to top. */
export function walkingLine(steps: readonly IStepGeometry[]): IStairPoint[] {
    return steps.map((step: IStepGeometry) => treadSides(step).centre);
}

/**
 * A railing in the stair's frame. The opening railing is built in room millimetres so it stays
 * put when the stair moves; the stair railings already stand in the stair's frame.
 */
export function inStairFrame(railing: IRailingGeometry, offset: IStairPoint): IRailingGeometry {
    if (railing.kind !== 'upper') {
        return railing;
    }
    const shift: (point: IVec3) => IVec3 = (point: IVec3): IVec3 => ({ x: point.x - offset.x, y: point.y, z: point.z - offset.z });
    const moved: (part: IBalusterGeometry) => IBalusterGeometry = (part: IBalusterGeometry): IBalusterGeometry => ({
        ...part,
        position: shift(part.position),
    });
    return {
        ...railing,
        posts: railing.posts.map(moved),
        balusters: railing.balusters.map(moved),
        handrail: { ...railing.handrail, points: railing.handrail.points.map(shift) },
    };
}

/**
 * The posts of the railings, each once, in railing order — the letters A, B, C… go to them
 * in this order. A post two railings share stands once.
 */
export function letteredPosts(railings: readonly IRailingGeometry[]): IBalusterGeometry[] {
    const posts: IBalusterGeometry[] = [];
    for (const post of railings.flatMap((railing: IRailingGeometry) => railing.posts)) {
        const seen: boolean = posts.some(
            (other: IBalusterGeometry) =>
                Math.abs(other.position.x - post.position.x) < 1 && Math.abs(other.position.z - post.position.z) < 1
        );
        if (!seen) {
            posts.push(post);
        }
    }
    return posts;
}

/**
 * The posts that can end a run of this railing: those of the railings on its level, and a post of
 * the other level its handrail ends on — the opening railing ends on the stair's top post where
 * the two meet. An opening post stands over the stair's wall railing in plan, yet the stair
 * handrail passes under it.
 */
export function levelPosts(railings: readonly IRailingGeometry[], railing: IRailingGeometry): IBalusterGeometry[] {
    const upper: boolean = railing.kind === 'upper';
    const points: IVec3[] = railing.handrail.points;
    const ends: IVec3[] = points.length > 0 ? [points[0], points[points.length - 1]] : [];
    const endsOn: (post: IBalusterGeometry, width: number) => boolean = (post: IBalusterGeometry, width: number): boolean =>
        ends.some((end: IVec3) => Math.hypot(end.x - post.position.x, end.z - post.position.z) <= width / 2 + 1);
    const other: IBalusterGeometry[] = railings
        .filter((candidate: IRailingGeometry) => (candidate.kind === 'upper') !== upper)
        .flatMap((candidate: IRailingGeometry) => candidate.posts.filter((post: IBalusterGeometry) => endsOn(post, candidate.postWidth)));
    return [...letteredPosts(railings.filter((candidate: IRailingGeometry) => (candidate.kind === 'upper') === upper)), ...other];
}

/** The letter of the n-th post: A…Z, then A1…Z1 and on. */
export function postLetter(index: number): string {
    const letter: string = String.fromCharCode(65 + (index % 26));
    const round: number = Math.floor(index / 26);
    return round === 0 ? letter : `${letter}${String(round)}`;
}

/** Where a plan point falls along a polyline: the plan distance from its start to the nearest point on it. */
function distanceAlong(points: readonly IVec3[], point: IStairPoint): { s: number; off: number } {
    let best: { s: number; off: number } = { s: 0, off: Infinity };
    let start: number = 0;
    for (let i: number = 1; i < points.length; i++) {
        const dx: number = points[i].x - points[i - 1].x;
        const dz: number = points[i].z - points[i - 1].z;
        const plan: number = Math.hypot(dx, dz);
        const t: number =
            plan > 0 ? Math.min(1, Math.max(0, ((point.x - points[i - 1].x) * dx + (point.z - points[i - 1].z) * dz) / (plan * plan))) : 0;
        const off: number = Math.hypot(points[i - 1].x + dx * t - point.x, points[i - 1].z + dz * t - point.z);
        if (off < best.off) {
            best = { s: start + plan * t, off };
        }
        start += plan;
    }
    return best;
}

/** The plan point a distance along a polyline. */
function pointAlong(points: readonly IVec3[], s: number): IStairPoint {
    let left: number = s;
    for (let i: number = 1; i < points.length; i++) {
        const plan: number = Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
        if (plan >= left && plan > 0) {
            const t: number = left / plan;
            return { x: points[i - 1].x + (points[i].x - points[i - 1].x) * t, z: points[i - 1].z + (points[i].z - points[i - 1].z) * t };
        }
        left -= plan;
    }
    return points[points.length - 1];
}

/** The plan distances along a railing's handrail of the posts standing on it, in order. */
export function postStations(railing: IRailingGeometry, posts: readonly IBalusterGeometry[]): number[] {
    return posts
        .map((post: IBalusterGeometry) => distanceAlong(railing.handrail.points, post.position))
        .filter(({ off }: { off: number }) => off <= railing.postWidth)
        .map(({ s }: { s: number }) => s)
        .sort((a: number, b: number) => a - b);
}

/**
 * The plan length of every run of a railing between two neighbouring post faces, written at the
 * run's middle: what the shop cuts the handrail and the baluster row to. Runs shorter than a post
 * — the handrail rising up a post — carry none. A corner post two railings share is listed under
 * one of them, so the posts of every railing in the same frame are passed: each one standing on
 * this handrail ends a run.
 */
export function railingRunMarks(railing: IRailingGeometry, posts: readonly IBalusterGeometry[]): IPlanMark[] {
    const line: IVec3[] = railing.handrail.points;
    const stations: number[] = postStations(railing, posts);
    const marks: IPlanMark[] = [];
    for (let i: number = 1; i < stations.length; i++) {
        const clear: number = stations[i] - stations[i - 1] - railing.postWidth;
        if (clear > railing.postWidth) {
            marks.push({ value: Math.round(clear), at: pointAlong(line, (stations[i] + stations[i - 1]) / 2) });
        }
    }
    return marks;
}

/** A run of ordinary treads one after another in one direction: a flight on the plan. */
export function flightOutlines(steps: readonly IStepGeometry[]): IStairPoint[][] {
    const flights: IStairPoint[][] = [];
    let current: IStepGeometry[] = [];
    const close: () => void = (): void => {
        if (current.length > 1) {
            flights.push(current.flatMap((step: IStepGeometry) => treadCorners(step)));
        }
        current = [];
    };
    for (const step of steps) {
        const ordinary: boolean = !step.isLanding && !step.isWinder && !step.winderCorners;
        if (!ordinary || (current.length > 0 && current[current.length - 1].rotation !== step.rotation)) {
            close();
        }
        if (ordinary) {
            current.push(step);
        }
    }
    close();
    return flights;
}

function markText(at: IPoint, value: string, font: IFont, color: EColorRef): ITextPrimitive {
    return {
        at,
        color,
        font,
        kind: EPrimitiveKind.Text,
        text: value,
        align: ETextAlign.Center,
        baseline: ETextBaseline.Middle,
        optional: true,
    };
}

/** The tread going at both edges of every tread and the posts and run lengths of every railing, the opening railing included. */
export function projectTreadMarks(
    result: IStairCalculationResult,
    railOn: boolean,
    offset: IStairPoint,
    projector: IPlanProjector,
    out: IPlanPrimitive[]
): void {
    const at: (point: IStairPoint) => IPoint = (point: IStairPoint): IPoint => [
        projector.pwx(offset.x + point.x),
        projector.pwy(offset.z + point.z),
    ];
    for (const mark of treadEdgeMarks(result.geometry.steps, result.treadDepth)) {
        out.push(markText(at(mark.at), String(mark.value), MARK_FONT, EColorRef.Ink3));
    }
    if (!railOn) {
        return;
    }
    // The stair railings first, then the opening railing: its posts take the letters after the stair's.
    const railings: IRailingGeometry[] = [
        ...result.geometry.railings.filter((railing: IRailingGeometry) => railing.kind !== 'upper'),
        ...result.geometry.railings.filter((railing: IRailingGeometry) => railing.kind === 'upper'),
    ].map((railing: IRailingGeometry) => inStairFrame(railing, offset));
    // A run length stands past the handrail, away from the middle of the stair: inside, the tread goings take that place.
    const line: IStairPoint[] = walkingLine(result.geometry.steps);
    const middle: IStairPoint = {
        x: line.reduce((sum: number, point: IStairPoint) => sum + point.x, 0) / Math.max(1, line.length),
        z: line.reduce((sum: number, point: IStairPoint) => sum + point.z, 0) / Math.max(1, line.length),
    };
    const posts: IBalusterGeometry[] = letteredPosts(railings);
    for (const mark of railings.flatMap((railing: IRailingGeometry) => railingRunMarks(railing, levelPosts(railings, railing)))) {
        const [x, y]: IPoint = at(mark.at);
        const [mx, my]: IPoint = at(middle);
        const away: number = Math.max(1, Math.hypot(x - mx, y - my));
        out.push(
            markText(
                [x + ((x - mx) / away) * RUN_MARK_GAP, y + ((y - my) / away) * RUN_MARK_GAP],
                String(mark.value),
                MARK_FONT,
                EColorRef.Handrail
            )
        );
    }
    const half: number = projector.pws(railings[0]?.postWidth ?? 0) / 2;
    posts.forEach((post: IBalusterGeometry, i: number) => {
        const [px, py]: IPoint = at(post.position);
        out.push({
            kind: EPrimitiveKind.Text,
            text: postLetter(i),
            at: [px + half + LETTER_GAP, py - half - LETTER_GAP],
            align: ETextAlign.Left,
            baseline: ETextBaseline.Bottom,
            color: EColorRef.Ink,
            font: LETTER_FONT,
        });
    });
}

/** An arrowhead at `tip`, pointing along the direction from `from`. */
function walkHead(from: IPoint, tip: IPoint): IPlanPrimitive {
    const angle: number = Math.atan2(tip[1] - from[1], tip[0] - from[0]);
    return {
        kind: EPrimitiveKind.Poly,
        points: [
            tip,
            [tip[0] - WALK_HEAD_LEN * Math.cos(angle - 0.4), tip[1] - WALK_HEAD_LEN * Math.sin(angle - 0.4)],
            [tip[0] - WALK_HEAD_LEN * Math.cos(angle + 0.4), tip[1] - WALK_HEAD_LEN * Math.sin(angle + 0.4)],
        ],
        closed: true,
        fill: EColorRef.Ink3,
    };
}

/** The walking line through the middle of every tread, with its arrow on the top tread. */
export function projectWalkingLine(
    steps: readonly IStepGeometry[],
    offset: IStairPoint,
    projector: IPlanProjector,
    out: IPlanPrimitive[]
): void {
    const path: IPoint[] = walkingLine(steps).map((point: IStairPoint): IPoint => [
        projector.pwx(offset.x + point.x),
        projector.pwy(offset.z + point.z),
    ]);
    if (path.length < 2) {
        return;
    }
    // Dashed as an axis: the walking line is a line of the drawing, not an edge of wood.
    out.push({ kind: EPrimitiveKind.Poly, points: path, closed: false, stroke: EColorRef.Ink4, width: 1, dash: WALK_DASH });
    out.push(walkHead(path[path.length - 2], path[path.length - 1]));
}

/** One flight's length: beside it on the side away from the stair's middle, along its run. */
function flightDim(
    outline: readonly IStairPoint[],
    middle: IStairPoint,
    offset: IStairPoint,
    projector: IPlanProjector,
    measurer: ITextMeasurer
): IPlanPrimitive {
    const xs: number[] = outline.map((point: IStairPoint) => point.x);
    const zs: number[] = outline.map((point: IStairPoint) => point.z);
    const minX: number = Math.min(...xs);
    const maxX: number = Math.max(...xs);
    const minZ: number = Math.min(...zs);
    const maxZ: number = Math.max(...zs);
    if (maxZ - minZ >= maxX - minX) {
        const left: boolean = (minX + maxX) / 2 <= middle.x;
        const x: number = projector.pwx(offset.x + (left ? minX : maxX)) + (left ? -FLIGHT_DIM_GAP : FLIGHT_DIM_GAP);
        return verticalDim(x, projector.pwy(offset.z + minZ), projector.pwy(offset.z + maxZ), String(Math.round(maxZ - minZ)), measurer);
    }
    const above: boolean = (minZ + maxZ) / 2 <= middle.z;
    const y: number = projector.pwy(offset.z + (above ? minZ : maxZ)) + (above ? -FLIGHT_DIM_GAP : FLIGHT_DIM_GAP);
    return horizontalDim(projector.pwx(offset.x + minX), y, projector.pwx(offset.x + maxX), String(Math.round(maxX - minX)), measurer);
}

/** The length of every flight, dimensioned outside the stair along its run. */
export function projectFlightLengths(
    steps: readonly IStepGeometry[],
    offset: IStairPoint,
    projector: IPlanProjector,
    measurer: ITextMeasurer,
    out: IPlanPrimitive[]
): void {
    const all: IStairPoint[] = steps.flatMap((step: IStepGeometry) => [...treadSides(step).sides.flat()]);
    if (all.length === 0) {
        return;
    }
    const middleX: number =
        (Math.min(...all.map((point: IStairPoint) => point.x)) + Math.max(...all.map((point: IStairPoint) => point.x))) / 2;
    const middleZ: number =
        (Math.min(...all.map((point: IStairPoint) => point.z)) + Math.max(...all.map((point: IStairPoint) => point.z))) / 2;
    const middle: IStairPoint = { x: middleX, z: middleZ };
    for (const outline of flightOutlines(steps)) {
        out.push(flightDim(outline, middle, offset, projector, measurer));
    }
}

/** A name and its value as one cell of the parameter rows. */
function cell(name: string, value: string): string {
    return `${name} ${value}`;
}

/** The stair's parameters in two rows over the plan, as the table on a stair maker's plan. */
export function projectParameterNotes(result: IStairCalculationResult, projector: IPlanProjector, out: IPlanPrimitive[]): void {
    const treads: number = result.geometry.steps.filter((step: IStepGeometry) => !step.isLanding).length;
    const rows: string[] = [
        [
            cell($localize`:@@planParamRises:Подъёмов`, String(result.stepCount)),
            cell($localize`:@@planParamTreads:Ступеней`, String(treads)),
            cell($localize`:@@planParamHeight:Высота`, String(Math.round(result.room.floorHeight))),
            cell($localize`:@@planParamAngle:Угол подъёма`, `${result.stairAngle.toFixed(1)}°`),
        ].join(' · '),
        [
            cell($localize`:@@planParamGoing:Проступь`, String(Math.round(result.treadDepth))),
            cell($localize`:@@planParamRise:Высота ступени`, result.riserHeight.toFixed(1)),
            cell('2h+b', String(Math.round(result.blondelValue))),
            cell($localize`:@@planParamClearance:Просвет`, String(Math.round(result.headroom))),
        ].join(' · '),
    ];
    rows.forEach((row: string, i: number) => {
        out.push({
            kind: EPrimitiveKind.Text,
            text: row,
            at: [projector.pwx(0), projector.pwy(0) + i],
            align: ETextAlign.Left,
            baseline: ETextBaseline.Top,
            color: EColorRef.Ink3,
            font: NOTE_FONT,
            role: ETextRole.Note,
        });
    });
}
